import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/theme";

const empty = { name: "", industry: "", location: "", website: "", primary_contact: "", phone: "", email: "", gst_number: "", assigned_salesperson: "", customer_status: "Prospect", notes: "" };

function Field({ label, required, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      {children}
    </div>
  );
}

export function CompanyFormDialog({ open, onOpenChange, company, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const editing = !!company;

  useEffect(() => {
    if (open) { setErr(""); setForm(company ? { ...empty, ...company } : empty); }
  }, [open, company]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { setErr("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_salesperson: form.assigned_salesperson || null };
      if (editing) { await api.put(`/companies/${company.id}`, payload); toast.success("Company updated"); }
      else { await api.post("/companies", payload); toast.success("Company added"); }
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-slate-100 px-6 py-4"><DialogTitle className="text-lg">{editing ? "Edit Company" : "Add Company"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <Field label="Company Name" required full>
            <Input data-testid="company-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Company legal name" />
            {err && <p className="text-xs text-red-500">{err}</p>}
          </Field>
          <Field label="Industry">
            <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
              <SelectTrigger data-testid="company-industry"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(meta?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Customer Status">
            <Select value={form.customer_status} onValueChange={(v) => set("customer_status", v)}>
              <SelectTrigger data-testid="company-status"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.customer_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Location"><Input data-testid="company-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, State" /></Field>
          <Field label="Website"><Input data-testid="company-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="www.company.com" /></Field>
          <Field label="Primary Contact"><Input data-testid="company-contact" value={form.primary_contact} onChange={(e) => set("primary_contact", e.target.value)} /></Field>
          <Field label="Phone"><Input data-testid="company-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" /></Field>
          <Field label="Email"><Input data-testid="company-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="GST Number"><Input data-testid="company-gst" value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} placeholder="22AAAAA0000A1Z5" /></Field>
          <Field label="Assigned Salesperson">
            <Select value={form.assigned_salesperson || ""} onValueChange={(v) => set("assigned_salesperson", v)}>
              <SelectTrigger data-testid="company-assigned"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(meta?.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role]}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Notes" full><Textarea data-testid="company-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="company-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : editing ? "Save" : "Add Company"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
